import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { IBankStatementDocument } from "@/jenesys-types/jenesys-entities/config";

interface BankStatementDocumentsState {
  statements: IBankStatementDocument[];
  isFetchingStatements: boolean;
  isUploading: boolean;
  isDownloadingCSV: boolean;
  isDownloadingPDF: boolean;
  error: string | null;
}

const initialState: BankStatementDocumentsState = {
  statements: [],
  isFetchingStatements: false,
  isUploading: false,
  isDownloadingCSV: false,
  isDownloadingPDF: false,
  error: null
};

interface LoadStatementsPayload {
  clientId: string;
  search: string;
}

interface DownloadCSVPayload {
  clientId: string;
  ids: string[];
}

interface DownloadPDFPayload {
  clientId: string;
  statementId: string;
}

interface UploadBankStatementsPayload {
  files: File[];
}

interface DownloadPDFFilesPayload {
  statements: IBankStatementDocument[];
}

interface DownloadCSVFilesPayload {
  statements: IBankStatementDocument[];
}

const BankStatementDocumentsSlice = createSlice({
  name: "features/bankStatementDocuments",
  initialState,
  reducers: {
    loadStatementsStart: {
      reducer: (state) => {
        state.isFetchingStatements = true;
        state.error = null;
      },
      prepare: (payload: LoadStatementsPayload) => ({
        payload
      })
    },
    loadStatementsSuccess: (
      state,
      action: PayloadAction<IBankStatementDocument[]>
    ) => {
      state.statements = action.payload;
      state.isFetchingStatements = false;
    },
    loadStatementsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isFetchingStatements = false;
    },
    uploadStatementsStart: {
      reducer: (state) => {
        state.isUploading = true;
        state.error = null;
      },
      prepare: (files: Array<File>) => ({
        payload: { files },
        meta: {
          onSuccess: {
            message: "Statements uploaded successfully"
          }
        }
      })
    },
    uploadStatementsSuccess: (state) => {
      state.isUploading = false;
    },
    uploadStatementsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isUploading = false;
    },
    downloadCSVStart: {
      reducer: (state) => {
        state.isDownloadingCSV = true;
        state.error = null;
      },
      prepare: (payload: DownloadCSVPayload) => ({
        payload
      })
    },
    downloadCSVSuccess: (state) => {
      state.isDownloadingCSV = false;
    },
    downloadCSVFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isDownloadingCSV = false;
    },
    downloadPDFStart: {
      reducer: (state) => {
        state.isDownloadingPDF = true;
        state.error = null;
      },
      prepare: (payload: DownloadPDFPayload) => ({
        payload
      })
    },
    downloadPDFSuccess: (state) => {
      state.isDownloadingPDF = false;
    },
    downloadPDFFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isDownloadingPDF = false;
    },
    uploadBankStatements: {
      reducer: (state) => {
        state.isUploading = true;
        state.error = null;
      },
      prepare: (payload: UploadBankStatementsPayload) => ({
        payload
      })
    },
    downloadPDFFiles: {
      reducer: (state) => {
        state.isDownloadingPDF = true;
        state.error = null;
      },
      prepare: (payload: DownloadPDFFilesPayload) => ({
        payload
      })
    },
    downloadCSVFiles: {
      reducer: (state) => {
        state.isDownloadingCSV = true;
        state.error = null;
      },
      prepare: (payload: DownloadCSVFilesPayload) => ({
        payload
      })
    }
  }
});

export const {
  loadStatementsStart,
  loadStatementsSuccess,
  loadStatementsFailure,
  uploadStatementsStart,
  uploadStatementsSuccess,
  uploadStatementsFailure,
  downloadCSVStart,
  downloadCSVSuccess,
  downloadCSVFailure,
  downloadPDFStart,
  downloadPDFSuccess,
  downloadPDFFailure,
  uploadBankStatements,
  downloadPDFFiles,
  downloadCSVFiles
} = BankStatementDocumentsSlice.actions;

export default BankStatementDocumentsSlice.reducer;
